import CRMStatusChip from '@components/Common/CRMStatusChip';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { REJECT_TITLE, isPendingRequest, rejectBody } from '@components/CompanyRequests/decision';
import { STATUS_PARAM } from '@components/Dashboard/attentionQueues';
import {
  approveCompanyRequest,
  companyById,
  companyRequestsList,
  employers as employersQuery,
  updateCompanyRequestStatus,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDate } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const REQUEST_COLUMNS = ['company', 'employer', 'requested', 'status', 'note', 'actions'];

const PAGE_SIZE = 25;

interface RequestRow {
  _id: string;
  companyId: string;
  employeeId: string;
  status: string;
  requestNote?: string;
  createdAt?: string;
  updatedAt?: string;
}

const fmt = (d?: string) => formatDate(d);

const NOTE_CHARS = 90;
const clip = (note?: string) => {
  const text = (note ?? '').trim();
  if (!text) return '-';
  return text.length > NOTE_CHARS ? `${text.slice(0, NOTE_CHARS)}...` : text;
};

/**
 * Every company access request, across companies.
 *
 * `/company-requests/:requestId` has existed and worked for as long as requests have, and there
 * was no way to reach it: an admin got the link from the employer's email or from a Mongo query.
 * Its own back button already pointed at `/companies`, which is a fair description of where it
 * expected to be reached from and was not.
 *
 * A tab rather than a page because a request is a claim about a company, and an admin reaches for
 * it in the same breath as the company list. A separate nav item would be a permanent entry for
 * something that is usually empty.
 *
 * Names have to be resolved client-side: the rows carry `companyId` and `employeeId` and no names
 * at all. Companies are looked up per visible row and cached, because company is high-cardinality
 * and `Jobs/index.tsx` already established that preloading them is the wrong trade. Employers come
 * from one bounded page, the `AgreementsList` pattern. It can miss, and an unmapped id renders as
 * the id rather than blanking the row.
 */
const CompanyRequestsList: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  /*
   * `?status=pending` narrows the list to one status: the dashboard's "Company requests" tile
   * links here, and its count is taken with the same filter.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = (searchParams.get(STATUS_PARAM) ?? '').trim().toLowerCase();
  const [deciding, setDeciding] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<RequestRow | null>(null);
  const [rows, setRows] = useState<RequestRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const [employerNames, setEmployerNames] = useState<Record<string, string>>({});

  // Survives paging, so stepping back through pages costs nothing and a company that appears on
  // two pages is fetched once.
  const companyCache = useRef<Record<string, string>>({});

  const load = useCallback(async (nextPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(
        companyRequestsList(
          nextPage,
          PAGE_SIZE,
          statusFilter ? [`{ key: "status", value: ${JSON.stringify(statusFilter)} }`] : [],
        ),
      );
      setRows(data?.getAllCompanyRequests?.content ?? []);
      setTotal(data?.getAllCompanyRequests?.totalElements ?? 0);
    } catch (e) {
      console.error('Failed to load company requests:', e);
      setError('Failed to load company requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const clearStatusFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(STATUS_PARAM);
    setSearchParams(next);
    setPage(0);
  };

  const nameOf = (row: RequestRow) => ({
    company: names[row.companyId] ?? 'this company',
    employer: employerNames[row.employeeId] ?? 'This employer',
  });

  /*
   * Approve from the row: the atomic approval, with no job transfer and no claim on unowned jobs.
   * Both of those are decisions that need the request page's context (who holds the company, how
   * many jobs have no owner), and both stay available there after approval, so the toast points
   * the admin at it rather than making the choice for them.
   */
  const approve = async (row: RequestRow) => {
    setDeciding(row._id);
    try {
      await fetchGraphQLApiData(approveCompanyRequest(row._id));
      const n = nameOf(row);
      showToast(`Approved. ${n.employer} is attached to ${n.company}. Open the request to move or assign its jobs.`, 'success');
      await load(page);
    } catch {
      showToast('Could not approve this request. Nothing was changed.', 'error');
    } finally {
      setDeciding(null);
    }
  };

  const reject = async () => {
    if (!rejecting) return;
    const row = rejecting;
    setDeciding(row._id);
    try {
      await fetchGraphQLApiData(updateCompanyRequestStatus(row._id, 'rejected'));
      setRejecting(null);
      showToast('Request rejected.', 'success');
      await load(page);
    } catch {
      showToast('Could not reject this request. Nothing was changed.', 'error');
    } finally {
      setDeciding(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const missing = Array.from(
      new Set(rows.map(r => r.companyId).filter(id => id && !companyCache.current[id])),
    );
    if (missing.length === 0) return;

    Promise.all(
      missing.map(id =>
        fetchGraphQLApiData(companyById(id))
          .then(({ data }) => [id, data?.getCompanyById?.companyName] as const)
          // One unresolvable company must not cost the other twenty-four their names.
          .catch(() => [id, undefined] as const),
      ),
    ).then(pairs => {
      for (const [id, name] of pairs) {
        if (name) companyCache.current[id] = name;
      }
      if (!cancelled) setNames({ ...companyCache.current });
    });

    return () => {
      cancelled = true;
    };
  }, [rows]);

  useEffect(() => {
    let cancelled = false;
    fetchGraphQLApiData(employersQuery(0, 200))
      .then(({ data }) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const e of data?.getAllEmployers?.content ?? []) {
          map[e._id] =
            e?.personalDetails?.fullName ||
            [e?.personalDetails?.firstName, e?.personalDetails?.lastName]
              .filter(Boolean)
              .join(' ') ||
            e?.personalDetails?.email ||
            e._id;
        }
        setEmployerNames(map);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading && rows.length === 0) return <SkeletonLoader rows={10} columns={REQUEST_COLUMNS.length} />;

  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
        <Typography color="error" variant="body2">{error}</Typography>
      </Box>
    );
  }

  /* Says what the list is narrowed to, with the way out beside it, so a filtered view reached from
     the dashboard never reads as the whole list. */
  const filterNote = statusFilter ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flexWrap: 'wrap', mb: 'var(--spacing-sm)' }}>
      <Typography color="text.secondary" variant="body2">
        {`Showing ${statusFilter} requests only.`}
      </Typography>
      <Button size="small" variant="text" onClick={clearStatusFilter}>
        Show all requests
      </Button>
    </Box>
  ) : null;

  if (!loading && total === 0) {
    return (
      <>
        {filterNote}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, py: 8 }}>
          <Typography color="text.secondary" variant="body2">
            {statusFilter
              ? `No ${statusFilter} company access requests.`
              : 'No company access requests yet. An employer creates one by claiming an existing company from their workspace.'}
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
    {filterNote}
    <MintlyTable
      pagination={{
        enabled: true,
        page,
        rowsPerPage: PAGE_SIZE,
        totalNumberOfRows: total,
        handleChangePage: (_e: unknown, next: number) => setPage(next),
      }}
      rows={rows.map(r => ({ ...r, id: r._id }))}
      selectors={REQUEST_COLUMNS}
      onRowClick={(row: RequestRow) => navigate(`/company-requests/${row._id}`)}
      tableHeadRow={{
        company: <>Company</>,
        employer: <>Requested by</>,
        requested: <>Requested</>,
        status: <>Status</>,
        note: <>Note</>,
        actions: <>Decision</>,
      }}
      tableBodyRow={{
        company: (row: RequestRow) => <>{names[row.companyId] ?? row.companyId}</>,
        employer: (row: RequestRow) => (
          <>{employerNames[row.employeeId] ?? row.employeeId}</>
        ),
        requested: (row: RequestRow) => <>{fmt(row.createdAt)}</>,
        status: (row: RequestRow) => <CRMStatusChip status={row.status} />,
        /* Clipped in JS with the full text on hover, rather than with `text-overflow`, because
           `Companies/index.scss` is not imported by anything and a class added there would render
           nothing. The note is the employer's case for why they work there and it is what decides
           the outcome, so it belongs on the row rather than only behind a click. */
        note: (row: RequestRow) => (
          <Tooltip title={row.requestNote || ''}>
            <span>{clip(row.requestNote)}</span>
          </Tooltip>
        ),
        /* The row opens the request, so the buttons stop the click and the Enter key from
           reaching it: deciding from the row must not also navigate away from it. */
        actions: (row: RequestRow) =>
          isPendingRequest(row.status) ? (
            <Box
              sx={{ display: 'flex', gap: 'var(--spacing-sm)', flexWrap: 'wrap' }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                variant="outlined"
                color="success"
                disabled={deciding != null}
                onClick={() => approve(row)}
                aria-label={`Approve the request from ${nameOf(row).employer} for ${nameOf(row).company}`}
              >
                {deciding === row._id && !rejecting ? 'Approving…' : 'Approve'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={deciding != null}
                onClick={() => setRejecting(row)}
                aria-label={`Reject the request from ${nameOf(row).employer} for ${nameOf(row).company}`}
              >
                Reject
              </Button>
            </Box>
          ) : (
            <>-</>
          ),
      }}
    />
    <ConfirmDialog
      open={rejecting != null}
      title={REJECT_TITLE}
      body={rejecting ? rejectBody(nameOf(rejecting).employer, nameOf(rejecting).company) : ''}
      actionLabel="Reject"
      busy={rejecting != null && deciding === rejecting._id}
      onCancel={() => setRejecting(null)}
      onConfirm={reject}
    />
    </>
  );
};

export default CompanyRequestsList;
